import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Avatar,
  IconButton,
  useDisclosure,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Grid,
  GridItem,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  useColorModeValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Progress,
  Flex,
  Spacer,
} from "@chakra-ui/react";
import {
  FiSearch,
  FiPlus,
  FiEye,
  FiEdit,
  FiTrash,
  FiSend,
  FiCheck,
  FiX,
  FiMessageSquare,
  FiFilter,
  FiBriefcase,
  FiUsers,
  FiTrendingUp,
  FiClock,
} from "react-icons/fi";
import { jobRequisitionService } from "../jobRequisitionService";
import JobRequisitionForm from "./JobRequisitionForm";

const JobRequisitions = () => {
  const [requisitions, setRequisitions] = useState([]);
  const [filteredRequisitions, setFilteredRequisitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRequisitions: 0,
    pendingApprovals: 0,
    approvedRequisitions: 0,
    rejectedRequisitions: 0,
  });

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");

  // Modals
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [approvalAction, setApprovalAction] = useState(null);
  const [approvalComments, setApprovalComments] = useState("");

  const {
    isOpen: isFormOpen,
    onOpen: onFormOpen,
    onClose: onFormClose,
  } = useDisclosure();
  const {
    isOpen: isViewOpen,
    onOpen: onViewOpen,
    onClose: onCloseView,
  } = useDisclosure();
  const {
    isOpen: isApprovalOpen,
    onOpen: onApprovalOpen,
    onClose: onApprovalClose,
  } = useDisclosure();

  const toast = useToast();
  const bgColor = useColorModeValue("white", "gray.800");

  useEffect(() => {
    fetchRequisitions();
    fetchStats();
  }, []);

  useEffect(() => {
    filterRequisitions();
  }, [
    requisitions,
    searchTerm,
    statusFilter,
    departmentFilter,
    priorityFilter,
  ]);

  const fetchRequisitions = async () => {
    try {
      setLoading(true);
      const response = await jobRequisitionService.getAllRequisitions();
      setRequisitions(response.requisitions || []);
    } catch (error) {
      console.error("Error fetching requisitions:", error);
      toast({
        title: "Error loading requisitions",
        description: "Could not load job requisitions",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const statsData = await jobRequisitionService.getRequisitionStats();

      const statusCounts = {};
      statsData.statusStats?.forEach((stat) => {
        statusCounts[stat._id] = stat.count;
      });

      setStats({
        totalRequisitions: Object.values(statusCounts).reduce(
          (a, b) => a + b,
          0
        ),
        pendingApprovals: statusCounts["Pending Approval"] || 0,
        approvedRequisitions: statusCounts["Approved"] || 0,
        rejectedRequisitions: statusCounts["Rejected"] || 0,
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const filterRequisitions = () => {
    let filtered = requisitions;

    if (searchTerm) {
      filtered = filtered.filter(
        (req) =>
          req.jobTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          req.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          req.requisitionId?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter) {
      filtered = filtered.filter((req) => req.status === statusFilter);
    }

    if (departmentFilter) {
      filtered = filtered.filter((req) => req.department === departmentFilter);
    }

    if (priorityFilter) {
      filtered = filtered.filter((req) => req.priority === priorityFilter);
    }

    setFilteredRequisitions(filtered);
  };

  const handleCreateRequisition = () => {
    setSelectedRequisition(null);
    onFormOpen();
  };

  const handleEditRequisition = (requisition) => {
    setSelectedRequisition(requisition);
    onFormOpen();
  };

  const handleViewRequisition = (requisition) => {
    setSelectedRequisition(requisition);
    onViewOpen();
  };

  const handleDeleteRequisition = async (requisition) => {
    if (
      !window.confirm(
        `Are you sure you want to delete requisition ${requisition.requisitionId}?`
      )
    ) {
      return;
    }

    try {
      await jobRequisitionService.deleteRequisition(requisition._id);
      toast({
        title: "Requisition deleted",
        description: "Job requisition has been deleted successfully",
        status: "success",
        duration: 3000,
      });
      fetchRequisitions();
      fetchStats();
    } catch (error) {
      toast({
        title: "Error deleting requisition",
        description: "Could not delete the requisition",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleSubmitForApproval = async (requisition) => {
    try {
      await jobRequisitionService.submitForApproval(requisition._id);
      toast({
        title: "Submitted for approval",
        description: "Requisition has been submitted for approval",
        status: "success",
        duration: 3000,
      });
      fetchRequisitions();
      fetchStats();
    } catch (error) {
      toast({
        title: "Error submitting for approval",
        description: "Could not submit the requisition",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleApprovalAction = (requisition, action) => {
    setSelectedRequisition(requisition);
    setApprovalAction(action);
    setApprovalComments("");
    onApprovalOpen();
  };

  const handleProcessApproval = async () => {
    try {
      await jobRequisitionService.processApproval(
        selectedRequisition._id,
        approvalAction,
        approvalComments
      );

      toast({
        title: `Requisition ${approvalAction}d`,
        description: `Job requisition has been ${approvalAction}d successfully`,
        status: "success",
        duration: 3000,
      });

      fetchRequisitions();
      fetchStats();
      onApprovalClose();
    } catch (error) {
      toast({
        title: `Error ${approvalAction}ing requisition`,
        description: "Could not process the approval",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleFormSuccess = () => {
    fetchRequisitions();
    fetchStats();
    onFormClose();
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "Draft":
        return "gray";
      case "Pending Approval":
        return "yellow";
      case "Approved":
        return "green";
      case "Rejected":
        return "red";
      case "On Hold":
        return "orange";
      case "Cancelled":
        return "red";
      case "Filled":
        return "blue";
      default:
        return "gray";
    }
  };

  const getPriorityBadgeColor = (priority) => {
    switch (priority) {
      case "Low":
        return "gray";
      case "Medium":
        return "blue";
      case "High":
        return "orange";
      case "Critical":
        return "red";
      default:
        return "gray";
    }
  };

  const getDepartments = () => {
    return [
      ...new Set(requisitions.map((req) => req.department).filter(Boolean)),
    ];
  };

  const canEditRequisition = (requisition) => {
    return requisition.status === "Draft";
  };

  const canDeleteRequisition = (requisition) => {
    return requisition.status === "Draft";
  };

  const canSubmitForApproval = (requisition) => {
    return requisition.status === "Draft";
  };

  const canApproveRequisition = (requisition) => {
    return (
      requisition.status === "Pending Approval" &&
      requisition.approvals?.some((approval) => approval.status === "Pending")
    );
  };

  if (loading) {
    return (
      <Box textAlign="center" py={12}>
        <Spinner size="xl" color="blue.500" mb={4} />
        <Text>Loading job requisitions...</Text>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center">
          <Box>
            <Text fontSize="2xl" fontWeight="bold">
              Job Requisitions & Approvals
            </Text>
            <Text color="gray.600">
              Manage hiring requests and approval workflows
            </Text>
          </Box>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            onClick={handleCreateRequisition}
          >
            Create Requisition
          </Button>
        </HStack>

        {/* Statistics Cards */}
        <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={4}>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Total Requisitions</StatLabel>
                <StatNumber>{stats.totalRequisitions}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Pending Approval</StatLabel>
                <StatNumber>{stats.pendingApprovals}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Approved</StatLabel>
                <StatNumber>{stats.approvedRequisitions}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Rejected</StatLabel>
                <StatNumber>{stats.rejectedRequisitions}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
        </Grid>

        {/* Filters */}
        <Card>
          <CardBody>
            <Grid
              templateColumns={{ base: "1fr", md: "repeat(5, 1fr)" }}
              gap={4}
            >
              <Box>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <FiSearch color="gray" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search requisitions..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Box>
              <Box>
                <Select
                  placeholder="All Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="Draft">Draft</option>
                  <option value="Pending Approval">Pending Approval</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                  <option value="On Hold">On Hold</option>
                  <option value="Cancelled">Cancelled</option>
                  <option value="Filled">Filled</option>
                </Select>
              </Box>
              <Box>
                <Select
                  placeholder="All Departments"
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  <option value="">All Departments</option>
                  {getDepartments().map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box>
                <Select
                  placeholder="All Priorities"
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                >
                  <option value="">All Priorities</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </Select>
              </Box>
              <Box>
                <Button
                  leftIcon={<FiFilter />}
                  variant="outline"
                  onClick={() => {
                    setSearchTerm("");
                    setStatusFilter("");
                    setDepartmentFilter("");
                    setPriorityFilter("");
                  }}
                >
                  Clear Filters
                </Button>
              </Box>
            </Grid>
          </CardBody>
        </Card>

        {/* Requisitions Table */}
        <Card>
          <CardBody>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Requisition</Th>
                  <Th>Job Title</Th>
                  <Th>Department</Th>
                  <Th>Priority</Th>
                  <Th>Status</Th>
                  <Th>Created</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredRequisitions.map((requisition) => (
                  <Tr key={requisition._id}>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="medium">
                          {requisition.requisitionId}
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          {requisition.numberOfPositions} position
                          {requisition.numberOfPositions > 1 ? "s" : ""}
                        </Text>
                      </VStack>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="medium">{requisition.jobTitle}</Text>
                        <Text fontSize="sm" color="gray.600">
                          {requisition.employmentType} • {requisition.jobLevel}
                        </Text>
                      </VStack>
                    </Td>
                    <Td>{requisition.department}</Td>
                    <Td>
                      <Badge
                        colorScheme={getPriorityBadgeColor(
                          requisition.priority
                        )}
                        variant="subtle"
                      >
                        {requisition.priority}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={getStatusBadgeColor(requisition.status)}
                        variant="subtle"
                      >
                        {requisition.status}
                      </Badge>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <Text fontSize="sm">
                          {new Date(requisition.createdAt).toLocaleDateString()}
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          by {requisition.createdByName}
                        </Text>
                      </VStack>
                    </Td>
                    <Td>
                      <HStack spacing={1}>
                        <IconButton
                          icon={<FiEye />}
                          size="sm"
                          variant="ghost"
                          onClick={() => handleViewRequisition(requisition)}
                          title="View Details"
                        />
                        {canEditRequisition(requisition) && (
                          <IconButton
                            icon={<FiEdit />}
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditRequisition(requisition)}
                            title="Edit"
                          />
                        )}
                        {canSubmitForApproval(requisition) && (
                          <IconButton
                            icon={<FiSend />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() => handleSubmitForApproval(requisition)}
                            title="Submit for Approval"
                          />
                        )}
                        {canApproveRequisition(requisition) && (
                          <>
                            <IconButton
                              icon={<FiCheck />}
                              size="sm"
                              variant="ghost"
                              colorScheme="green"
                              onClick={() =>
                                handleApprovalAction(requisition, "approve")
                              }
                              title="Approve"
                            />
                            <IconButton
                              icon={<FiX />}
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() =>
                                handleApprovalAction(requisition, "reject")
                              }
                              title="Reject"
                            />
                          </>
                        )}
                        {canDeleteRequisition(requisition) && (
                          <IconButton
                            icon={<FiTrash />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => handleDeleteRequisition(requisition)}
                            title="Delete"
                          />
                        )}
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>

            {filteredRequisitions.length === 0 && (
              <Box textAlign="center" py={8}>
                <Text color="gray.500">
                  {requisitions.length === 0
                    ? "No job requisitions found"
                    : "No requisitions match your filters"}
                </Text>
              </Box>
            )}
          </CardBody>
        </Card>
      </VStack>

      {/* Create/Edit Form Modal */}
      <JobRequisitionForm
        isOpen={isFormOpen}
        onClose={onFormClose}
        requisition={selectedRequisition}
        onSuccess={handleFormSuccess}
      />

      {/* View Details Modal */}
      <Modal isOpen={isViewOpen} onClose={onCloseView} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Requisition Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedRequisition && (
              <VStack spacing={6} align="stretch">
                <Grid
                  templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                  gap={4}
                >
                  <Box>
                    <Text fontWeight="bold">Requisition ID</Text>
                    <Text>{selectedRequisition.requisitionId}</Text>
                  </Box>
                  <Box>
                    <Text fontWeight="bold">Status</Text>
                    <Badge
                      colorScheme={getStatusBadgeColor(
                        selectedRequisition.status
                      )}
                      variant="subtle"
                    >
                      {selectedRequisition.status}
                    </Badge>
                  </Box>
                  <Box>
                    <Text fontWeight="bold">Job Title</Text>
                    <Text>{selectedRequisition.jobTitle}</Text>
                  </Box>
                  <Box>
                    <Text fontWeight="bold">Department</Text>
                    <Text>{selectedRequisition.department}</Text>
                  </Box>
                  <Box>
                    <Text fontWeight="bold">Priority</Text>
                    <Badge
                      colorScheme={getPriorityBadgeColor(
                        selectedRequisition.priority
                      )}
                      variant="subtle"
                    >
                      {selectedRequisition.priority}
                    </Badge>
                  </Box>
                  <Box>
                    <Text fontWeight="bold">Employment Type</Text>
                    <Text>{selectedRequisition.employmentType}</Text>
                  </Box>
                </Grid>

                {selectedRequisition.businessJustification && (
                  <Box>
                    <Text fontWeight="bold">Business Justification</Text>
                    <Text>{selectedRequisition.businessJustification}</Text>
                  </Box>
                )}

                {/* Approval Progress */}
                {selectedRequisition.approvals &&
                  selectedRequisition.approvals.length > 0 && (
                    <Box>
                      <Text fontWeight="bold" mb={3}>
                        Approval Progress
                      </Text>
                      <VStack spacing={3} align="stretch">
                        {selectedRequisition.approvals.map(
                          (approval, index) => (
                            <HStack key={index} spacing={3}>
                              <Badge
                                colorScheme={
                                  approval.status === "Approved"
                                    ? "green"
                                    : approval.status === "Rejected"
                                    ? "red"
                                    : "yellow"
                                }
                                variant="subtle"
                                minW="80px"
                              >
                                {approval.status}
                              </Badge>
                              <Text flex="1">
                                {approval.approverName} ({approval.approverRole}
                                )
                              </Text>
                              {approval.approvedAt && (
                                <Text fontSize="sm" color="gray.600">
                                  {new Date(
                                    approval.approvedAt
                                  ).toLocaleDateString()}
                                </Text>
                              )}
                            </HStack>
                          )
                        )}
                      </VStack>
                    </Box>
                  )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onCloseView}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Approval Modal */}
      <Modal isOpen={isApprovalOpen} onClose={onApprovalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {approvalAction === "approve" ? "Approve" : "Reject"} Requisition
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text>
                Are you sure you want to {approvalAction} the requisition{" "}
                <strong>{selectedRequisition?.requisitionId}</strong> for{" "}
                <strong>{selectedRequisition?.jobTitle}</strong>?
              </Text>

              <FormControl>
                <FormLabel>Comments (Optional)</FormLabel>
                <Textarea
                  value={approvalComments}
                  onChange={(e) => setApprovalComments(e.target.value)}
                  placeholder="Add any comments..."
                  rows={3}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onApprovalClose}>
              Cancel
            </Button>
            <Button
              colorScheme={approvalAction === "approve" ? "green" : "red"}
              onClick={handleProcessApproval}
              ml={3}
            >
              {approvalAction === "approve" ? "Approve" : "Reject"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default JobRequisitions;
